/**
 * Quotations API Service
 * Handles all quotation-related API calls
 */

import API_BASE_URL from './apiConfig';
import { getAuthHeaders } from './authApi';
import { formatDate } from '../utils/dateUtils';

/**
 * Transform backend quotation data to frontend format
 */
const transformQuotation = (quotation) => {
  const revisions = quotation.revision || [];
  const lastRevision = revisions.length > 0 ? revisions[revisions.length - 1] : null;
  
  return {
    id: quotation._id,
    sn: 0, // Will be set based on index
    quotationNumber: quotation.quotation_number_main || quotation.quotations?.quotation_number || quotation._id,
    status: quotation.current_status || quotation.quotations?.current_status || '',
    lastUpdatedOn: formatDate(quotation.updatedAt),
    clientName: quotation.client_name || '',
    location: quotation.city || quotation.full_address?.city || '',
    initialQuotationDate: quotation.quotations?.quotation_date || '',
    revisedQuotationDate: lastRevision?.quotation_date || null,
    quotationValue: quotation.quotations?.quotation_amt || 0,
    revisions: revisions.length,
    revisionHistory: [
      {
        revision: 1,
        date: quotation.quotations?.quotation_date || '',
        value: quotation.quotations?.quotation_amt || 0,
        status: quotation.quotations?.current_status || quotation.current_status || '',
      },
      ...revisions.map((rev, idx) => ({
        revision: rev.revision_id || idx + 2,
        date: rev.quotation_date || '',
        value: rev.quotation_amt || 0,
        status: rev.current_status || '',
      })),
    ],
    // Additional fields
    quotation_number_main: quotation.quotation_number_main,
    client_id: quotation.client_id?._id || quotation.client_id,
    enquiry_id: quotation.enquiry_id?._id || quotation.enquiry_id,
  };
};

/**
 * Transform frontend quotation data to backend format
 */
const transformQuotationForBackend = (quotation) => {
  return {
    quotation_number_main: quotation.quotation_number_main,
    client_name: quotation.client_name || quotation.clientName,
    client_id: quotation.client_id,
    client_email: quotation.client_email,
    client_mobile: quotation.client_mobile,
    contact_person_name: quotation.contact_person_name,
    contact_person_designation: quotation.contact_person_designation,
    full_address: quotation.full_address,
    city: quotation.city || quotation.location,
    state: quotation.state,
    enquiry_id: quotation.enquiry_id,
    enquiry_number: quotation.enquiry_number,
    medium: quotation.medium,
    source: quotation.source,
    source_comment: quotation.source_comment,
    current_status: quotation.current_status || quotation.status,
    is_order_received: quotation.is_order_received,
    quotations: quotation.quotations,
    revision: quotation.revision,
  };
};

/**
 * List quotations with filters, sorting, and pagination
 */
export const listQuotations = async (filters = {}, sort = {}, page = 0, rowsPerPage = 10) => {
  try {
    const params = new URLSearchParams({
      page: (page + 1).toString(),
      limit: rowsPerPage.toString(),
    });

    if (filters.client_name || filters.clientName) {
      params.append('client_name', filters.client_name || filters.clientName);
    }
    if (filters.status) {
      params.append('status', filters.status);
    }
    if (filters.quotation_number || filters.quotationNumber) {
      params.append('quotation_number', filters.quotation_number || filters.quotationNumber);
    }

    const response = await fetch(`${API_BASE_URL}/quotation?${params.toString()}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: `HTTP error! status: ${response.status}` }));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    const quotations = result.data || [];
    const transformed = quotations.map((quotation, index) => ({
      ...transformQuotation(quotation),
      sn: page * rowsPerPage + index + 1,
    }));

    return {
      data: transformed,
      total: result.total || 0,
      page,
      rowsPerPage,
    };
  } catch (error) {
    console.error('Error fetching quotations:', error);
    throw error;
  }
};

/**
 * Get single quotation by ID
 */
export const getQuotationById = async (id) => {
  try {
    const response = await fetch(`${API_BASE_URL}/quotation/${id}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: `HTTP error! status: ${response.status}` }));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    const quotation = await response.json();
    return Array.isArray(quotation) ? transformQuotation(quotation[0]) : transformQuotation(quotation);
  } catch (error) {
    console.error('Error fetching quotation:', error);
    throw error;
  }
};

/**
 * Create new quotation
 */
export const createQuotation = async (data) => {
  try {
    const payload = transformQuotationForBackend({
      ...data,
      clientName: data.clientName,
      location: data.location,
      current_status: data.status,
      quotations: {
        quotation_amt: parseFloat(data.quotationValue) || 0,
        quotation_date: new Date().toISOString().split('T')[0],
        current_status: data.status,
      },
    });
    
    const response = await fetch(`${API_BASE_URL}/quotation`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: `HTTP error! status: ${response.status}` }));
      throw new Error(errorData.message || 'Failed to create quotation');
    }

    const result = await response.json();

    return {
      success: true,
      message: 'Quotation created successfully',
      data: transformQuotation(result),
    };
  } catch (error) {
    console.error('Error creating quotation:', error);
    return {
      success: false,
      error: error.message || 'Failed to create quotation',
    };
  }
};

/**
 * Update quotation
 */
export const updateQuotation = async (id, updates) => {
  try {
    const payload = transformQuotationForBackend(updates);
    
    const response = await fetch(`${API_BASE_URL}/quotation/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: `HTTP error! status: ${response.status}` }));
      throw new Error(errorData.message || 'Failed to update quotation');
    }

    const result = await response.json();

    return {
      success: true,
      data: transformQuotation(result),
    };
  } catch (error) {
    console.error('Error updating quotation:', error);
    return {
      success: false,
      error: error.message || 'Failed to update quotation',
    };
  }
};

/**
 * Delete quotation
 */
export const deleteQuotation = async (id) => {
  try {
    const response = await fetch(`${API_BASE_URL}/quotation/${id}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
    });

    if (!response.ok) {
      throw new Error('Failed to delete quotation');
    }

    return {
      success: true,
    };
  } catch (error) {
    console.error('Error deleting quotation:', error);
    return {
      success: false,
      error: error.message || 'Failed to delete quotation',
    };
  }
};

/**
 * Mark quotation as ordered (transition to order)
 */
export const markOrdered = async (id) => {
  try {
    const response = await fetch(`${API_BASE_URL}/quotation/mark-ordered/${id}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'Failed to mark ordered');
    }

    const result = await response.json();
    return {
      success: true,
      message: result.message || 'Order created successfully',
      data: result.order || result,
    };
  } catch (error) {
    console.error('Error marking quotation ordered:', error);
    return {
      success: false,
      error: error.message || 'Failed to mark ordered',
    };
  }
};

/**
 * Get revision history for a quotation
 */
export const getRevisionHistory = async (quotationNumber) => {
  try {
    // First, find the quotation by number
    const listResponse = await listQuotations({ quotation_number: quotationNumber }, {}, 0, 1);
    
    if (listResponse.data.length === 0) {
      return [];
    }

    const quotation = listResponse.data[0];
    return quotation.revisionHistory || [];
  } catch (error) {
    console.error('Error fetching revision history:', error);
    return [];
  }
};

// Export as default object for backward compatibility
const quotationsApi = {
  list: listQuotations,
  get: getQuotationById,
  create: createQuotation,
  update: updateQuotation,
  delete: deleteQuotation,
  markOrdered,
  getRevisionHistory,
};

export default quotationsApi;
